import { Input } from '@cargoflow/ui/components/input'
import type { Treaty } from '@elysiajs/eden'
import { useQuery } from '@tanstack/react-query'
import { Check, Loader2, MapPin, X } from 'lucide-react'
import { useCallback, useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { match, P } from 'ts-pattern'
import { api } from '@/lib/api'
import { queryKeys } from '@/lib/query-keys'
import { cn } from '@/lib/utils'
import { CountryFlag } from './country-flag'

export type Location = Treaty.Data<typeof api.locations.search.get>['locations'][number]

export interface LocationValue {
  city: string
  country_code: string
  county?: string | null
}

interface LocationSearchProps {
  value: LocationValue | null
  onSelect: (location: Location) => void
  onClear?: () => void
  placeholder?: string
  disabled?: boolean
  className?: string
}

export function LocationSearch({
  value,
  onSelect,
  onClear,
  placeholder,
  disabled,
  className,
}: LocationSearchProps) {
  const { t } = useTranslation('common')
  const containerRef = useRef<HTMLDivElement>(null)
  const inputRef = useRef<HTMLInputElement>(null)
  const [inputValue, setInputValue] = useState('')
  const [isEditing, setIsEditing] = useState(false)
  const [isOpen, setIsOpen] = useState(false)
  const [debouncedInput, setDebouncedInput] = useState('')
  const [highlightedIndex, setHighlightedIndex] = useState(-1)

  useEffect(() => {
    const timer = setTimeout(() => setDebouncedInput(inputValue), 300)
    return () => clearTimeout(timer)
  }, [inputValue])

  // Close and restore on outside click
  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setIsOpen(false)
        setIsEditing(false)
        setInputValue('')
        setHighlightedIndex(-1)
      }
    }
    document.addEventListener('mousedown', handler)
    return () => document.removeEventListener('mousedown', handler)
  }, [])

  const { data, isLoading } = useQuery({
    queryKey: queryKeys.locations.search(debouncedInput),
    queryFn: async () => {
      const { data, error } = await api.locations.search.get({
        query: { q: debouncedInput },
      })
      if (error) throw error
      return data
    },
    enabled: debouncedInput.length >= 2,
    staleTime: 30_000,
  })

  const locations = data?.locations ?? []
  const searchPlaceholder = placeholder ?? t('routes.fields.search_city')

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setInputValue(e.target.value)
    setIsOpen(true)
    setHighlightedIndex(-1) // Reset highlighted index when typing
  }

  const handleSelect = useCallback(
    (location: Location) => {
      onSelect(location)
      setIsOpen(false)
      setIsEditing(false)
      setInputValue('')
      setHighlightedIndex(-1)
    },
    [onSelect],
  )

  const handleClear = () => {
    onClear?.()
    setInputValue('')
    setIsEditing(false)
    setIsOpen(false)
    setHighlightedIndex(-1)
  }

  const startEditing = () => {
    setIsEditing(true)
    setIsOpen(true)
    setTimeout(() => inputRef.current?.focus(), 0)
  }

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLInputElement>) => {
      if (!isOpen || locations.length === 0) return

      switch (e.key) {
        case 'ArrowDown':
          e.preventDefault()
          setHighlightedIndex((prev) => (prev < locations.length - 1 ? prev + 1 : 0))
          break
        case 'ArrowUp':
          e.preventDefault()
          setHighlightedIndex((prev) => (prev > 0 ? prev - 1 : locations.length - 1))
          break
        case 'Enter':
          e.preventDefault()
          if (highlightedIndex >= 0 && highlightedIndex < locations.length) {
            handleSelect(locations[highlightedIndex])
          }
          break
        case 'Escape':
          e.preventDefault()
          setIsOpen(false)
          setIsEditing(false)
          setInputValue('')
          setHighlightedIndex(-1)
          break
      }
    },
    [isOpen, locations, highlightedIndex, handleSelect],
  )

  const showDropdown = isOpen && (debouncedInput.length >= 2 || isLoading)

  return (
    <div ref={containerRef} className={cn('relative w-full', className)}>
      {/* Selected value display or search input */}
      {match({ value, isEditing })
        .with({ value: P.nonNullable, isEditing: false }, ({ value: v }) => (
          <div className="relative">
            <button
              type="button"
              onClick={startEditing}
              disabled={disabled}
              className="h-9 w-full flex items-center gap-2 px-3 pr-8 border border-input rounded-md bg-background text-sm hover:bg-accent/50 transition-colors"
            >
              <MapPin className="h-4 w-4 shrink-0 text-muted-foreground" />
              <span className="flex-1 truncate text-left">
                {v.city}
                {v.county && <span className="text-muted-foreground ml-1">· {v.county}</span>}
              </span>
              <CountryFlag countryCode={v.country_code} className="w-5 h-3.5 shrink-0 opacity-80" />
            </button>
            {onClear && (
              <button
                type="button"
                onClick={handleClear}
                className="absolute right-2 top-1/2 -translate-y-1/2 p-0.5 hover:bg-muted rounded-sm transition-colors"
                tabIndex={-1}
              >
                <X className="h-3.5 w-3.5 opacity-50 hover:opacity-100" />
              </button>
            )}
          </div>
        ))
        .otherwise(() => (
          <div className="relative">
            <MapPin className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground pointer-events-none" />
            <Input
              ref={inputRef}
              value={inputValue}
              onChange={handleInputChange}
              onFocus={() => {
                if (inputValue.length >= 2) setIsOpen(true)
              }}
              onKeyDown={handleKeyDown}
              placeholder={searchPlaceholder}
              disabled={disabled}
              className="pl-9"
              role="combobox"
              aria-expanded={showDropdown}
              aria-haspopup="listbox"
              aria-autocomplete="list"
            />
          </div>
        ))}

      {/* Dropdown */}
      {showDropdown && (
        <div
          className="absolute z-50 w-full mt-1 bg-popover border border-border rounded-md shadow-lg max-h-60 overflow-auto"
          role="listbox"
        >
          {isLoading && (
            <div className="flex items-center justify-center py-4">
              <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
            </div>
          )}

          {!isLoading && locations.length === 0 && (
            <div className="py-4 text-center text-sm text-muted-foreground">
              {t('routes.fields.no_locations_found')}
            </div>
          )}

          {locations.map((location, index) => {
            const isSelected =
              value?.city === location.city && value?.country_code === location.country_code
            const isHighlighted = index === highlightedIndex

            return (
              <button
                key={location.id}
                type="button"
                role="option"
                aria-selected={isSelected}
                onMouseDown={(e) => {
                  e.preventDefault()
                  handleSelect(location)
                }}
                onMouseEnter={() => setHighlightedIndex(index)}
                className={cn(
                  'w-full flex items-center gap-2 px-3 py-2 text-left text-sm',
                  isHighlighted ? 'bg-accent' : 'hover:bg-accent',
                )}
              >
                <Check
                  className={cn('h-3.5 w-3.5 shrink-0', isSelected ? 'opacity-100' : 'opacity-0')}
                />
                <span className="flex-1 truncate">
                  {location.city}
                  {location.county && (
                    <span className="text-muted-foreground ml-1">· {location.county}</span>
                  )}
                </span>
                <CountryFlag countryCode={location.country_code} className="w-5 h-3.5 shrink-0" />
              </button>
            )
          })}
        </div>
      )}
    </div>
  )
}
